import { useCompactStudyLayout } from './studyMobile';
import { ArrowLeft, Info, Wind } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  GAS_MODE_YEARS,
  GAS_MODEL_BOUNDARIES,
  GAS_MODES,
  GAS_SOURCES,
  MOLAR_VOLUMES,
  type GasMode,
} from './gasLaws';
import { GasLawsExploreLab } from './GasLawsExploreLab';
import { GasLawsExplorer } from './GasLawsExplorer';
import { GasLawsProblems } from './GasLawsProblems';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  GasMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  boyle: {
    tab: 'Boyle’s law',
    eyebrow: 'Pressure and volume',
    title: 'Squeeze the gas: p₁V₁ = p₂V₂',
    description:
      'Keep temperature and amount fixed. Predict how pressure changes as the piston moves.',
  },
  charles: {
    tab: 'Charles’s law',
    eyebrow: 'Volume and temperature',
    title: 'Heat the gas: V₁ ÷ T₁ = V₂ ÷ T₂',
    description: 'Keep pressure and amount fixed. Find out why the temperature must be in kelvin.',
  },
  ideal: {
    tab: 'Ideal gas equation',
    eyebrow: 'All four quantities',
    title: 'Link p, V, n and T: pV = nRT',
    description:
      'Convert every quantity to kPa, L, mol and K, then solve for the one you do not know.',
  },
};

type GasView = 'explore' | 'practice';

const VIEW_COPY: Record<GasView, string> = {
  explore: 'Explore',
  practice: 'Practice',
};

export function GasLawsLabPage() {
  const compact = useCompactStudyLayout();
  const [introOpen, setIntroOpen] = useState(false);
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<GasMode>(() => modeForYear(GAS_MODE_YEARS, year));
  const [view, setView] = useState<GasView>('explore');
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'gas-laws', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chemistry tools
      </Link>

      <header className="chemistry-tool-header gas-lab-header mt-5 flex flex-col gap-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:flex-row lg:items-end lg:justify-between lg:p-8">
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
            <Wind className="h-4 w-4" aria-hidden="true" /> Gases &amp; quantities
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Gas Laws Lab
          </h1>
          <details className="science-lab-intro" open={!compact || introOpen} onToggle={(event) => { if (compact) setIntroOpen(event.currentTarget.open); }}>
          <summary className="min-h-11 cursor-pointer py-3 text-sm font-bold text-ink-soft">About this lab</summary>
          <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
            Move a piston, heat a gas and watch the particles. Predict each graph before the model
            draws it, then solve gas-law problems with the working shown.
          </p>
          </details>
        </div>
        <div className="rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-ink-soft">
          Explore tool · no mastery claim
        </div>
      </header>

      <div
        className="gas-law-tabs mt-6 flex max-w-full flex-wrap gap-1 rounded-[1.5rem] border border-hairline bg-canvas-pure p-1 shadow-card-soft sm:inline-flex sm:rounded-full"
        role="tablist"
        aria-label="Gas law"
      >
        {GAS_MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={mode === candidate}
            onClick={() => setMode(candidate)}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold ${mode === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {MODE_COPY[candidate].tab}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          {copy.eyebrow}
        </p>
        <h2 className="mt-1 text-2xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 font-medium text-slate2">{copy.description}</p>
      </div>

      <div
        className="mt-5 inline-flex rounded-full bg-surface p-1"
        role="group"
        aria-label="Explore or practise"
      >
        {(Object.keys(VIEW_COPY) as GasView[]).map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={view === candidate}
            onClick={() => setView(candidate)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {VIEW_COPY[candidate]}
          </button>
        ))}
      </div>

      <div key={`${mode}-${view}`} className="mt-5 space-y-6">
        {view === 'explore' ? (
          <GasLawsExploreLab mode={mode} />
        ) : (
          <>
            <GasLawsExplorer mode={mode} />
            <GasLawsProblems mode={mode} />
          </>
        )}
      </div>

      <section aria-label="Reference conditions" className="mt-6 grid gap-4 md:grid-cols-2">
        {Object.values(MOLAR_VOLUMES).map((data) => (
          <div
            key={data.condition}
            className="rounded-[1.5rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft"
          >
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              {data.label}
            </p>
            <p className="mt-2 text-2xl font-extrabold text-ink">
              V<sub>m</sub> = {data.litresPerMol} L mol⁻¹
            </p>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">
              {data.temperatureK} K, {data.pressureKPa} kPa. {data.note}
            </p>
          </div>
        ))}
      </section>

      <section className="mt-6 rounded-[1.5rem] border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
        <p className="flex items-center gap-2 font-extrabold text-ink">
          <Info className="h-4 w-4" aria-hidden="true" /> Model boundaries and sources
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {GAS_MODEL_BOUNDARIES.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>Progress is not saved. Values are pending teaching review.</li>
        </ul>
        <p className="mt-3">
          Sources: {GAS_SOURCES.map((source) => `${source.label} — ${source.detail}`).join(' ')}
        </p>
      </section>
    </StudyShell>
  );
}
